import { FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { isCostRow } from "@/lib/budgetRows";
import { downloadXlsxFrom } from "@/components/HistoricalsMatrix";
import { VarianceNoteCell } from "@/components/budget/VarianceNoteCell";
import { downloadRealisationPdf } from "@/components/budget/realisationPdf";

// Begroting vs realisatie: één jaar met zowel een begroting als een vastgestelde jaarrekening.
export function RealisationView({ t, clubId, club, userTier, canEdit, years, year, onYear, rows, budget, actual, eur, fmtEurDelta, fmtPct, onSaved }) {
  if (!years.length) {
    return (
      <div className="border-2 border-dashed border-foreground/40 p-8 text-center" data-testid="realisation-empty">
        <div className="font-display uppercase font-bold text-lg">{t("Nog geen jaar met begroting én jaarrekening")}</div>
        <p className="text-muted-foreground text-sm mt-1 max-w-lg mx-auto">
          {t("Zodra de jaarrekening van een begroot jaar is ingevoerd bij Voorgaande jaren, zie je hier per post en subpost hoeveel de begroting afweek van de werkelijke cijfers.")}
        </p>
      </div>
    );
  }
  const val = (row, obj) => (obj ? row.value(obj) : null);
  const deltaCls = (row, de, dp) => {
    if (de === null || de === 0) return "";
    const favourable = isCostRow(row) ? de < 0 : de > 0;
    const big = dp !== null && Math.abs(dp) >= 20;
    return `${favourable ? "text-[#00A047]" : "text-destructive"} ${big ? "font-bold" : ""}`;
  };
  const notes = budget?.variance_notes || {};
  const nettoRow = rows.find((r) => r.key === "netto");
  const nettoB = nettoRow ? val(nettoRow, budget) : null;
  const nettoA = nettoRow ? val(nettoRow, actual) : null;
  const nettoD = nettoB === null || nettoA === null ? null : nettoA - nettoB;

  const saveNote = async (key, text) => {
    try {
      await api.patch(`/budgets/${budget.id}/variance-notes`, { notes: { [key]: text } });
      toast.success(text ? t("Toelichting opgeslagen") : t("Toelichting verwijderd"));
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
      throw e;
    }
  };
  const exportExcel = () => downloadXlsxFrom(`/clubs/${clubId}/budgets/realisatie.xlsx?year=${year}`, "export", t, clubId);
  const exportPdf = async () => {
    try {
      await downloadRealisationPdf({ t, club, clubId, year, rows, budget, actual, userTier });
      toast.success(t("PDF gedownload"));
    } catch (e) {
      toast.error(t("PDF maken mislukt") + (e?.message ? `: ${e.message}` : ""));
    }
  };

  return (
    <div className="space-y-3" data-testid="realisation-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-display uppercase font-black text-2xl tracking-tight">{t("Begroting vs realisatie")}</div>
          <p className="text-sm text-muted-foreground max-w-3xl">
            {t("Per post en subpost: wat je begrootte, wat het werd en het verschil (realisatie − begroting). Leg bij grote afwijkingen een korte toelichting vast — die gaat mee in de PDF en Excel voor de jaarvergadering.")}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="stat-label block" htmlFor="real-year">{t("Jaar")}</label>
            <select id="real-year" value={year ?? ""} onChange={(e) => onYear(parseInt(e.target.value, 10))}
              data-testid="realisation-year-picker" className="mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button onClick={exportExcel} data-testid="realisation-excel-btn"
            className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" /> {t("Excel")}
          </button>
          <button onClick={exportPdf} data-testid="realisation-pdf-btn"
            className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2">
            <FileText className="w-4 h-4" /> {t("PDF")}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="realisation-summary">
        {[
          ["begroot", t("Netto exploitatieresultaat begroot"), eur(nettoB), ""],
          ["gerealiseerd", t("Netto exploitatieresultaat gerealiseerd"), eur(nettoA), ""],
          ["verschil", t("Verschil"), fmtEurDelta(nettoD), nettoD === null || nettoD === 0 ? "" : nettoD > 0 ? "text-[#00A047]" : "text-destructive"],
        ].map(([k, label, value, cls]) => (
          <div key={k} className="border-2 border-foreground bg-card p-3" data-testid={`realisation-summary-${k}`}>
            <div className="stat-label">{label}</div>
            <div className={`font-mono-num text-xl font-bold mt-1 ${cls}`}>{value}</div>
          </div>
        ))}
      </div>
      <div className="border-2 border-foreground bg-card heavy-shadow overflow-x-auto">
        <table className="w-full text-sm" data-testid="realisation-table">
          <thead className="bg-muted/50 border-b-2 border-foreground">
            <tr>
              <th className="text-left px-3 py-2 stat-label sticky left-0 sticky-tint-50 min-w-[150px] md:min-w-[220px]">{t("Post")}</th>
              <th className="text-right px-3 py-2 stat-label min-w-[140px]" data-testid="real-col-budget">{t("Begroting")} {year}</th>
              <th className="text-right px-3 py-2 stat-label min-w-[140px] bg-muted/70" data-testid="real-col-actual">{t("Realisatie")} {year}</th>
              <th className="text-right px-3 py-2 stat-label min-w-[120px]">{t("Verschil €")}</th>
              <th className="text-right px-3 py-2 stat-label min-w-[100px]">{t("Verschil %")}</th>
              <th className="text-left px-3 py-2 stat-label min-w-[220px] md:min-w-[280px]" data-testid="real-col-note">{t("Toelichting")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const b = val(row, budget);
              const a = val(row, actual);
              const de = b === null || a === null ? null : a - b;
              const dp = b === null || a === null || !b ? null : ((a - b) / Math.abs(b)) * 100;
              const muted = row.kind === "sub" || row.kind === "sys";
              const trCls = row.kind === "computed" ? "border-t-2 border-foreground bg-muted/40" : muted ? "border-b border-foreground/5 bg-muted/10" : "border-b border-foreground/10";
              const labelCls = muted ? "pl-8 pr-3 py-1.5 sticky left-0 sticky-tint-10 text-sm text-muted-foreground italic"
                : row.kind === "computed" ? "px-3 py-2 sticky left-0 sticky-tint-40 font-display uppercase font-black"
                : "px-3 py-2 sticky left-0 bg-card font-bold";
              const cell = `text-right px-3 py-1.5 font-mono-num ${muted ? "text-sm text-muted-foreground" : ""} ${row.strong ? "font-bold" : ""}`;
              const dCls = deltaCls(row, de, dp);
              const dCell = dCls ? `text-right px-3 py-1.5 font-mono-num ${muted ? "text-sm" : ""} ${row.strong ? "font-bold" : ""} ${dCls}` : cell;
              return (
                <tr key={row.key} className={trCls} data-testid={`real-row-${row.key}`}>
                  <td className={labelCls}>{row.label}</td>
                  <td className={cell} data-testid={`real-${row.key}-budget`}>{eur(b)}</td>
                  <td className={`${cell} bg-muted/20`} data-testid={`real-${row.key}-actual`}>{eur(a)}</td>
                  <td className={dCell} data-testid={`real-${row.key}-delta-eur`}>{fmtEurDelta(de)}</td>
                  <td className={dCell} data-testid={`real-${row.key}-delta-pct`}>{fmtPct(dp)}</td>
                  <td className="px-3 py-1.5 align-top" data-testid={`real-${row.key}-note`}>
                    <VarianceNoteCell t={t} rowKey={row.key} text={notes[row.key] || ""} canEdit={canEdit}
                      hint={dp !== null && Math.abs(dp) >= 20} onSave={(text) => saveNote(row.key, text)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="realisation-legend">
        {t("Groen = gunstig (meer inkomsten of lagere kosten dan begroot), rood = ongunstig; vet zodra de afwijking ≥ 20% is.")}
        {canEdit && ` ${t("Bij een afwijking van 20% of meer zonder toelichting verschijnt de knop Verklaar. Toelichtingen kun je ook op een vergrendelde begroting vastleggen.")}`}
      </p>
    </div>
  );
}
